import { Component, inject, OnInit, signal, ViewChild, ViewContainerRef, ComponentRef, DestroyRef, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { finalize, catchError } from 'rxjs/operators';
import { of } from 'rxjs';

import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { ToastModule } from 'primeng/toast';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { SelectModule } from 'primeng/select';
import { PaginatorModule } from 'primeng/paginator';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';

import { PageLayoutComponent } from '../../shared/page-layout/page-layout';
import { ConfirmationService, MessageService } from 'primeng/api';
import { SideDrawerComponent } from '../../shared/side-drawer/side-drawer.component';
import { StateContainerComponent, ViewState } from '../../shared/state-container/state-container.component';
import { 
  IdentityGroupsService, 
  UserGroup, 
  PolicyDropdownItem, 
  CreateUserGroupRequest, 
  UpdateUserGroupRequest,
  User
} from '../../../service/identity-groups/identity-groups.service';

interface IdentityGroup {
  id?: number;
  name: string;
  type: 'Dynamic' | 'Static' | 'Synced';
  members: number;
  rule: string;
  auth: string;
  policy: string;
  policyId?: number | null;
  scope: string[];
  createdDate?: Date | string;
  assignedUsers?: any[];
  authenticationRequirement?: string;
  policyBaseline?: any;
}

@Component({
  selector: 'app-identity-groups',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    TableModule,
    InputTextModule,
    ButtonModule,
    DialogModule,
    ToastModule,
    ConfirmDialogModule,
    TooltipModule,
    IconFieldModule,
    InputIconModule,
    SelectModule,
    PaginatorModule,
    CardModule,
    TagModule,
    PageLayoutComponent,
    StateContainerComponent,
  ],
  templateUrl: './identity-groups.component.html',
  providers: [MessageService, ConfirmationService]
})
export class IdentityGroupsComponent implements OnInit {
  @ViewChild('drawerContainer', { read: ViewContainerRef }) drawerContainer!: ViewContainerRef;

  private fb = inject(FormBuilder);
  private messageService = inject(MessageService);
  private confirmationService = inject(ConfirmationService);
  private router = inject(Router);
  private identityGroupsService = inject(IdentityGroupsService);
  private destroyRef = inject(DestroyRef);

  groups = signal<IdentityGroup[]>([]);
  filteredGroups = signal<IdentityGroup[]>([]);
  selectedGroups = signal<IdentityGroup[]>([]);
  loading = signal(false);
  error = signal<any>(null);
  isSubmitting = false;
  searchTerm = '';
  typeFilter: string | null = null;
  showDialog = false;
  dialogMode: 'create' | 'edit' = 'create';
  editingGroup: IdentityGroup | null = null;

  // Policy dropdown related
  policyDropdownItems: PolicyDropdownItem[] = [];
  isLoadingPolicies = false;
  policyOptions: { label: string; value: number }[] = [];
  policyScopeMapping: Map<number, string[]> = new Map();

  // Side drawer
  private drawerRef: ComponentRef<SideDrawerComponent> | null = null;
  currentGroupForMembers: IdentityGroup | null = null;

  groupForm!: FormGroup;

  typeOptions = [
    { label: 'All Types', value: null },
    { label: 'Dynamic', value: 'Dynamic' },
    { label: 'Static', value: 'Static' },
    { label: 'Synced', value: 'Synced' }
  ];

  membershipTypeOptions = [
    { label: 'Dynamic', value: 'Dynamic' },
    { label: 'Static', value: 'Static' },
    { label: 'Synced', value: 'Synced' }
  ];

  authOptions = [
    { label: 'Mobile OTP', value: 'MOBILE_OTP' },
    { label: 'Email OTP', value: 'EMAIL_OTP' },
    { label: 'User Name Password', value: 'USERNAME_PASSWORD' },
    { label: 'Client Credentials', value: 'CLIENT_CREDENTIALS' },
    { label: '2FA - User Cred + Google Auth', value: 'TWO_FA_USERCRED_GOOGLE_AUTH' }
  ];

  authDisplayMap = new Map<string, string>([
    ['EMAIL_OTP', 'Email OTP'],
    ['MOBILE_OTP', 'Mobile OTP'],
    ['USERNAME_PASSWORD', 'User Name Password'],
    ['CLIENT_CREDENTIALS', 'Client Credentials'],
    ['TWO_FA_USERCRED_GOOGLE_AUTH', '2FA - User Cred + Google Auth']
  ]);
  
  readonly viewState = computed<ViewState>(() => {
    if (this.loading()) return 'loading';
    if (this.error()) return 'error';
    if (this.filteredGroups().length === 0) return 'empty';
    return 'content';
  });

  readonly showClearFilters = computed(() => {
    return !!(this.searchTerm || this.typeFilter);
  });

  readonly emptyStateMessage = computed(() => {
    if (this.searchTerm || this.typeFilter) {
      return 'No groups match your current search or filter criteria. Try adjusting your filters.';
    }
    return 'Create your first user group to organize users and service identities for policy assignment.';
  });

  readonly errorMessage = computed(() => {
    const err = this.error();
    if (typeof err === 'string') return err;
    if (err?.message) return err.message;
    return 'Failed to load user groups. Please try again.';
  });

  getVisibleScopes(scopes: string[]): string[] {
    return (scopes || []).slice(0, 3);
  }

  getRemainingScopesCount(scopes: string[]): number {
    return Math.max(0, (scopes || []).length - 3);
  }

  getRemainingScopesTooltip(scopes: string[]): string {
    return (scopes || []).slice(3).join(', ');
  }

  ruleAttributes = [
    { label: 'Identity type', value: 'identity.type' },
    { label: 'Partner category', value: 'partner.category' },
    { label: 'Territory', value: 'territory' },
    { label: 'Source system', value: 'source.system' },
    { label: 'Account status', value: 'account.status' }
  ];

  ruleOperators = [
    { label: 'Equals', value: '=' },
    { label: 'Does not equal', value: '!=' },
    { label: 'Contains', value: 'contains' },
    { label: 'Is one of', value: 'in' }
  ];

  ruleValues = [
    { label: 'Dealer', value: 'DEALER' },
    { label: 'Vendor', value: 'VENDOR' },
    { label: 'Employee', value: 'EMPLOYEE' },
    { label: 'Partner', value: 'PARTNER' },
    { label: 'Machine identity', value: 'MACHINE' },
    { label: 'Active', value: 'ACTIVE' }
  ];

  ngOnInit() {
    this.initForm();
    this.loadUserGroups();
  }

  initForm() {
    this.groupForm = this.fb.group({
      name: ['', Validators.required],
      type: ['Dynamic', Validators.required],
      ruleAttribute: ['identity.type'],
      ruleOperator: ['='],
      ruleValue: ['DEALER'],
      auth: ['MOBILE_OTP', Validators.required],
      policyId: [null],
      policyName: ['']
    });
  }

  loadUserGroups() {
    this.loading.set(true);
    this.error.set(null);
    this.identityGroupsService.getUserGroups()
      .pipe(
        finalize(() => {
          this.loading.set(false);
        }),
        catchError((error) => {
          console.error('Failed to load user groups', error);
          this.error.set(error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load user groups'
          });
          return of([]);
        })
      )
      .subscribe({
        next: (apiGroups: UserGroup[]) => {
          const transformedGroups = this.transformApiGroups(apiGroups);
          this.groups.set(transformedGroups);
          this.filterGroups();
          this.loadPolicies();
        }
      });
  }

  // Retry method for error state
  retryLoad() {
    this.loadUserGroups();
  }

  private transformApiGroups(apiGroups: UserGroup[]): IdentityGroup[] {
    return apiGroups.map(group => ({
      id: group.id,
      name: group.name,
      type: this.getGroupType(group.policyBaseline?.targetType || 'STATIC') as 'Dynamic' | 'Static' | 'Synced',
      members: group.memberCount || 0,
      rule: group.policyBaseline?.targetType || 'Manual membership',
      auth: this.authDisplayMap.get(group.authenticationRequirement) || group.authenticationRequirement,
      policy: group.policyBaseline?.name || 'No policy attached',
      policyId: group.policyBaseline?.id || null,
      scope: group.policyBaseline?.scopes || [],
      createdDate: group.createdDate,
      authenticationRequirement: group.authenticationRequirement,
      policyBaseline: group.policyBaseline,
      assignedUsers: []
    }));
  }

  private getGroupType(targetType: string): string {
    const typeMap: { [key: string]: string } = {
      'DYNAMIC': 'Dynamic',
      'STATIC': 'Static',
      'SYNCED': 'Synced'
    };
    return typeMap[targetType] || 'Static';
  }

  loadPolicies() {
    this.isLoadingPolicies = true;
    this.identityGroupsService.getPoliciesDropdown()
      .pipe(
        finalize(() => {
          this.isLoadingPolicies = false;
        }),
        catchError((error) => {
          console.error('Failed to load policies', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load policies'
          });
          return of([]);
        })
      )
      .subscribe({
        next: (policies: PolicyDropdownItem[]) => {
        const identityGroupPolicies = policies.filter(
          policy => policy.targetType === 'Identity Group' || policy.targetType === 'User Group'
        );
          
          this.policyDropdownItems = identityGroupPolicies;
          this.policyOptions = identityGroupPolicies.map(p => ({
            label: p.name,
            value: p.id
          }));
          
          this.policyScopeMapping = new Map();
          identityGroupPolicies.forEach(p => {
            this.policyScopeMapping.set(p.id, p.scopes || []);
          });
          
          const currentPolicyId = this.groupForm.get('policyId')?.value;
          if (currentPolicyId && !identityGroupPolicies.some(p => p.id === currentPolicyId)) {
            this.groupForm.patchValue({ policyId: null });
            this.onPolicyChange();
          }
        }
      });
  }

  onPolicyChange() {
    const policyId = this.groupForm.get('policyId')?.value;
    if (policyId) {
      const scopes = this.policyScopeMapping.get(policyId) || [];
      const selectedPolicy = this.policyOptions.find(p => p.value === policyId);
      if (selectedPolicy) {
        this.groupForm.patchValue({
          policyName: selectedPolicy.label
        });
      }
      if (this.dialogMode === 'edit' && this.editingGroup) {
        this.editingGroup.scope = [...scopes];
      }
    } else {
      this.groupForm.patchValue({
        policyName: ''
      });
      if (this.dialogMode === 'edit' && this.editingGroup) {
        this.editingGroup.scope = [];
      }
    }
  }

  filterGroups() {
    let filtered = this.groups();

    if (this.searchTerm) {
      const term = this.searchTerm.toLowerCase();
      filtered = filtered.filter(g =>
        g.name.toLowerCase().includes(term) ||
        g.policy.toLowerCase().includes(term) ||
        g.auth.toLowerCase().includes(term)
      );
    }

    if (this.typeFilter) {
      filtered = filtered.filter(g => g.type === this.typeFilter);
    }

    this.filteredGroups.set(filtered);
  }

  onSearch() {
    this.filterGroups();
  }

  loadGroups(page: number) {
    this.filterGroups();
  }

  refreshSelection() {
    this.selectedGroups.set([]);
  }

  clearFilters() {
    this.searchTerm = '';
    this.typeFilter = null;
    this.filterGroups();
  }

  openCreateGroupDialog() {
    this.dialogMode = 'create';
    this.editingGroup = null;
    this.groupForm.reset({
      name: '',
      type: 'Dynamic',
      ruleAttribute: 'identity.type',
      ruleOperator: '=',
      ruleValue: 'DEALER',
      auth: 'MOBILE_OTP',
      policyId: null,
      policyName: ''
    });
    this.showDialog = true;
  }

  configureGroup(group: IdentityGroup) {
    this.dialogMode = 'edit';
    this.editingGroup = { ...group };
    
    this.groupForm.patchValue({
      name: group.name,
      type: group.type,
      auth: group.authenticationRequirement || group.auth,
      policyId: group.policyId,
      policyName: group.policy,
    });
    
    this.showDialog = true;
  }

  // ==================== MEMBER MANAGEMENT METHODS ====================

  manageMembers(group: IdentityGroup) {
    this.currentGroupForMembers = group;
    this.openDrawer(group);
  }

  private openDrawer(group: IdentityGroup) {
    this.drawerRef = this.drawerContainer.createComponent(SideDrawerComponent);
    
    this.drawerRef.instance.visible = true;
    this.drawerRef.instance.title = `Manage Members - ${group.name}`;
    this.drawerRef.instance.subtitle = `${group.members} members in this group`;
    this.drawerRef.instance.users.set(group.assignedUsers || []);
    this.drawerRef.instance.groupId = group.id!;
    
    // Load members from API
    this.loadGroupMembers(group.id!, group);
    
    this.drawerRef.instance.visibleChange.subscribe((visible: boolean) => {
      if (!visible) {
        this.destroyDrawer();
      }
    });
    
    this.drawerRef.instance.userAdded.subscribe((user: User) => {
      this.handleUserAdded(group, user);
    });
    
    this.drawerRef.instance.userRemoved.subscribe((user: User) => {
      this.handleUserRemoved(group, user);
    });
  }

  private loadGroupMembers(groupId: number, group: IdentityGroup) {
    this.identityGroupsService.getGroupMembers(groupId)
      .pipe(
        catchError((error) => {
          console.error('Failed to load group members', error);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load group members'
          });
          return of(null);
        })
      )
      .subscribe({
        next: (response: any | null) => {
          if (response && this.drawerRef) {
            // Update drawer with members
            this.drawerRef.instance.users.set(response.members);
            this.drawerRef.instance.subtitle = `${response.memberCount} members in this group`;
            
            // Update the group's member count
            group.members = response.memberCount;
            this.updateGroupMemberCount(groupId, response.memberCount);
            
            // Store members for later use
            group.assignedUsers = response.members;
          }
        }
      });
  }

  private handleUserAdded(group: IdentityGroup, user: User) {
    // Update local state (the API call is already made in the drawer)
    const currentCount = group.members || 0;
    this.groups.update(groups =>
      groups.map(g => {
        if (g.id === group.id) {
          const updatedUsers = [...(g.assignedUsers || []), user];
          return {
            ...g,
            assignedUsers: updatedUsers,
            members: updatedUsers.length
          };
        }
        return g;
      })
    );
    this.filterGroups();
    this.updateGroupMemberCount(group.id!, currentCount + 1);
  }

  private handleUserRemoved(group: IdentityGroup, user: User) {
    // Update local state (the API call is already made in the drawer)
    const currentCount = group.members || 0;
    this.groups.update(groups =>
      groups.map(g => {
        if (g.id === group.id) {
          const updatedUsers = (g.assignedUsers || []).filter(u => u.id !== user.id);
          return {
            ...g,
            assignedUsers: updatedUsers,
            members: updatedUsers.length
          };
        }
        return g;
      })
    );
    this.filterGroups();
    this.updateGroupMemberCount(group.id!, Math.max(0, currentCount - 1));
  }

  private updateGroupMemberCount(groupId: number, count: number) {
    this.groups.update(groups =>
      groups.map(g => {
        if (g.id === groupId) {
          return { ...g, members: count };
        }
        return g;
      })
    );
    this.filterGroups();
  }

  private destroyDrawer() {
    if (this.drawerRef) {
      this.drawerRef.destroy();
      this.drawerRef = null;
      this.currentGroupForMembers = null;
    }
  }

  // ==================== END MEMBER MANAGEMENT ====================

  saveGroup() {
    if (this.groupForm.invalid) {
      this.groupForm.markAllAsTouched();
      return;
    }

    const formValue = this.groupForm.value;
    const payload: CreateUserGroupRequest = {
      name: formValue.name,
      authenticationRequirement: formValue.auth,
      policyId: formValue.policyId || 0,
    };

    this.isSubmitting = true;

    if (this.dialogMode === 'create') {
      this.identityGroupsService.createUserGroup(payload)
        .pipe(
          finalize(() => {
            this.isSubmitting = false;
          }),
          catchError((error) => {
            console.error('Failed to create user group', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to create user group'
            });
            return of(null);
          })
        )
        .subscribe({
          next: (response) => {
            if (response) {
              const newGroup = this.transformApiGroups([response])[0];
              this.groups.update(g => [...g, newGroup]);
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `Identity group "${newGroup.name}" created successfully`
              });
              this.closeDialog();
              this.filterGroups();
              this.loadPolicies();
            }
          }
        });
    } else if (this.dialogMode === 'edit' && this.editingGroup?.id) {
      const updatePayload: UpdateUserGroupRequest = {
        name: formValue.name,
        authenticationRequirement: formValue.auth,
        policyId: formValue.policyId || 0,
      };

      this.identityGroupsService.updateUserGroup(this.editingGroup.id, updatePayload)
        .pipe(
          finalize(() => {
            this.isSubmitting = false;
          }),
          catchError((error) => {
            console.error('Failed to update user group', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: 'Failed to update user group'
            });
            return of(null);
          })
        )
        .subscribe({
          next: (response) => {
            if (response) {
              const updatedGroup = this.transformApiGroups([response])[0];
              this.groups.update(groups =>
                groups.map(g =>
                  g.id === this.editingGroup?.id ? { ...g, ...updatedGroup } : g
                )
              );
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: `Identity group "${updatedGroup.name}" updated successfully`
              });
              this.closeDialog();
              this.filterGroups();
              this.loadPolicies();
            }
          }
        });
    }
  }

  closeDialog() {
    this.showDialog = false;
    this.editingGroup = null;
  }

  getScopeDisplay(scopes: string[]): string {
    if (!scopes || scopes.length === 0) {
      return 'No scopes assigned';
    }
    return scopes.join(', ');
  }
}